import { Head, Link } from "@inertiajs/react"
import type { ReactNode } from "react"

import MatchLayout from "@/layouts/match-layout"
import { matchPath } from "@/routes"

interface Team {
  id: string
  name: string
  short_name: string
  color: string
  score: number
}

interface MatchSummary {
  id: number
  date: string
  kickoff: string
  venue: string
  status: string
  competition: string | null
  has_video: boolean
  home_team: Team
  away_team: Team
}

interface Props {
  matches: MatchSummary[]
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    scheduled: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    live: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    complete: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status] || styles.complete}`}
    >
      {status}
    </span>
  )
}

export default function MatchesIndex({ matches }: Props) {
  return (
    <>
      <Head title="Matches" />
      <div className="bg-card border-b">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <h1 className="text-2xl font-bold tracking-tight">Matches</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            All match results and fixtures
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-6">
        {matches.length === 0 ? (
          <div className="text-muted-foreground py-12 text-center">
            No matches found
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {matches.map((m) => (
              <Link
                key={m.id}
                href={matchPath(m.id)}
                className="group bg-card rounded-lg border p-4 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex w-36 items-center gap-2.5">
                      <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-[10px] font-bold text-white"
                        style={{ backgroundColor: m.home_team.color }}
                      >
                        {m.home_team.short_name}
                      </div>
                      <span className="truncate text-sm font-semibold">
                        {m.home_team.name}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 tabular-nums">
                      <span className="text-lg font-black">
                        {m.home_team.score}
                      </span>
                      <span className="text-muted-foreground">–</span>
                      <span className="text-lg font-black">
                        {m.away_team.score}
                      </span>
                    </div>

                    <div className="flex w-36 items-center gap-2.5">
                      <div
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-[10px] font-bold text-white"
                        style={{ backgroundColor: m.away_team.color }}
                      >
                        {m.away_team.short_name}
                      </div>
                      <span className="truncate text-sm font-semibold">
                        {m.away_team.name}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1">
                    <StatusBadge status={m.status} />
                    <div className="flex items-center gap-1.5">
                      {m.has_video && (
                        <span title="Video available">
                          <svg
                            className="text-muted-foreground h-3.5 w-3.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                            />
                          </svg>
                        </span>
                      )}
                      <span className="text-muted-foreground text-xs">
                        {m.date}
                      </span>
                    </div>
                  </div>
                </div>

                {m.competition && (
                  <div className="text-muted-foreground mt-2 text-xs">
                    {m.competition} · {m.kickoff} · {m.venue}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

MatchesIndex.layout = (page: ReactNode) => <MatchLayout>{page}</MatchLayout>
